import { Alert } from 'react-native';
import { convertGiftedChatToHistory } from '@components/modals/HelpModal/utils/handleHelp';
import handleAiHelp from '@components/modals/HelpModal/utils/handleHelp';
import type { DomEditorHandle } from '@navigation/screens/LowLevel/Components/DomEditor';

export interface EditorHandlersConfig {
  showEditor: boolean;
  editorRef: React.RefObject<DomEditorHandle>;
  chatMessages: any[];
  globalUser: any;
  isEditorConnected: boolean;
  setInputDisabled: (disabled: boolean) => void;
}

/**
 * Send message to editor
 */
export const createHandleSendToEditor = (
  editorRef: React.RefObject<DomEditorHandle>,
  setEditorContent: (content: string) => void
) => {
  return (messageText: string, format: 'plain' | 'html' = 'html') => {
    console.log('📝 [editorHandlers] createHandleSendToEditor handler called', {
      messageLength: messageText?.length || 0,
      format,
      hasEditorRef: !!editorRef,
      hasEditorRefCurrent: !!editorRef?.current,
      editorRefType: typeof editorRef,
      editorRefKeys: editorRef ? Object.keys(editorRef) : [],
    });

    if (!editorRef.current) {
      console.error('❌ [editorHandlers] Editor ref is null or current is null', {
        editorRef,
        editorRefType: typeof editorRef,
        hasEditorRef: !!editorRef,
        editorRefValue: editorRef,
      });
      Alert.alert('Editor Not Open', 'Please open the editor first to send content.');
      return;
    }

    console.log('✅ [editorHandlers] Editor ref is valid, processing content', {
      hasGetContent: typeof editorRef.current.getContent === 'function',
      hasSetContent: typeof editorRef.current.setContent === 'function',
    });

    try {
      let content = messageText;
      console.log('🔄 [editorHandlers] Processing content format', { format, originalLength: messageText?.length || 0 });

      if (format === 'html' && messageText) {
        content = messageText
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code>$1</code>')
          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
          .replace(/^# (.*$)/gim, '<h1>$1</h1>')
          .replace(/\n/g, '<br>');
        console.log('✅ [editorHandlers] HTML formatting applied', { formattedLength: content?.length || 0 });
      }

      console.log('📖 [editorHandlers] Getting current editor content...');
      const current = editorRef.current.getContent() || '';
      console.log('📖 [editorHandlers] Current editor content retrieved', {
        currentLength: current?.length || 0,
        hasCurrentContent: current.trim().length > 0,
      });

      const sep = current.trim() ? '<br><br>' : '';
      const newContent = current + sep + content;
      console.log('📝 [editorHandlers] Setting new content to editor', {
        newContentLength: newContent?.length || 0,
        separator: sep,
      });

      editorRef.current.setContent(newContent);
      console.log('✅ [editorHandlers] Editor setContent called successfully');
      
      setEditorContent(newContent);
      console.log('✅ [editorHandlers] setEditorContent callback called');
    } catch (error) {
      console.error('❌ [editorHandlers] Error sending to editor', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        editorRef,
        hasEditorRef: !!editorRef,
        hasEditorRefCurrent: !!editorRef?.current,
      });
      Alert.alert('Error', 'Failed to send content to editor.');
    }
  };
};

/**
 * Send conversation summary to editor
 */
export const createHandleSendSummaryToEditor = (config: EditorHandlersConfig & {
  handleSendToEditor: (messageText: string, format?: 'plain' | 'html') => void;
  theme: boolean;
}) => {
  const {
    showEditor,
    editorRef,
    chatMessages,
    globalUser,
    isEditorConnected,
    setInputDisabled,
    handleSendToEditor,
  } = config;

  return async () => {
    if (!showEditor || !editorRef.current) {
      Alert.alert('Editor Not Open', 'Please open the editor first to send summary.');
      return;
    }

    try {
      const meaningfulMessages = chatMessages.filter(
        (msg) =>
          msg.text &&
          msg.text.trim().length > 0 &&
          !msg.isDisclaimer &&
          !msg.formSchema &&
          !msg.actionCards &&
          !msg.showOnboardingForm &&
          !msg.showProfileForm &&
          !msg.showBusinessModelSelection &&
          !msg.showModelConfirmation
      );

      if (meaningfulMessages.length === 0) {
        Alert.alert('No Content', 'No conversation content to summarize.');
        return;
      }

      const conversationText = meaningfulMessages
        .map((msg) => {
          const role = msg.user._id === globalUser ? 'User' : 'Assistant';
          return `${role}: ${msg.text}`;
        })
        .join('\n\n');

      setInputDisabled(true);
      const summaryPrompt = `Please provide a concise summary of the following conversation:\n\n${conversationText}\n\nSummary:`;

      await handleAiHelp(
        isEditorConnected || showEditor,
        summaryPrompt,
        convertGiftedChatToHistory(chatMessages),
        [],
        'ask',
        null,
        null
      )
        .then(async (response: any) => {
          const responseAny = response as any;
          const hasStreamingBody =
            responseAny &&
            (responseAny instanceof Response ||
              (responseAny.body && typeof responseAny.body.getReader === 'function') ||
              typeof responseAny.getReader === 'function');

          if (hasStreamingBody) {
            const reader = responseAny.body?.getReader
              ? responseAny.body.getReader()
              : responseAny.getReader
              ? responseAny.getReader()
              : null;

            if (reader) {
              const decoder = new TextDecoder();
              let accumulatedData = '';
              let summaryText = '';

              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                accumulatedData += decoder.decode(value, { stream: true });
                const lines = accumulatedData.split('\n');
                accumulatedData = lines.pop() || '';

                for (const line of lines) {
                  if (!line.trim()) continue;
                  try {
                    const data = JSON.parse(line);
                    const responseText =
                      typeof data.response === 'string' ? data.response : JSON.stringify(data.response);
                    summaryText += responseText;
                  } catch (err) {
                    // Skip invalid JSON
                  }
                }
              }

              if (accumulatedData) {
                try {
                  const data = JSON.parse(accumulatedData);
                  const responseText =
                    typeof data.response === 'string' ? data.response : JSON.stringify(data.response);
                  summaryText += responseText;
                } catch (err) {
                  // Skip
                }
              }

              if (summaryText.trim()) {
                handleSendToEditor(`<h2>Conversation Summary</h2><p>${summaryText}</p>`, 'html');
              }
            }
          } else {
            const jsonResponse = response as any;
            const summaryText =
              typeof jsonResponse.response === 'string'
                ? jsonResponse.response
                : JSON.stringify(jsonResponse.response || '');

            if (summaryText.trim()) {
              handleSendToEditor(`<h2>Conversation Summary</h2><p>${summaryText}</p>`, 'html');
            }
          }
        })
        .catch((err) => {
          console.error('Error generating summary:', err);
          Alert.alert('Error', 'Failed to generate summary.');
        })
        .finally(() => {
          setInputDisabled(false);
        });
    } catch (error) {
      console.error('Error sending summary to editor:', error);
      Alert.alert('Error', 'Failed to send summary to editor.');
      setInputDisabled(false);
    }
  };
};

/**
 * Send entire conversation to editor
 */
export const createHandleSendConversationToEditor = (config: EditorHandlersConfig & {
  handleSendToEditor: (messageText: string, format?: 'plain' | 'html') => void;
  theme: boolean;
}) => {
  const { showEditor, editorRef, chatMessages, globalUser, theme, handleSendToEditor } = config;

  return () => {
    if (!showEditor || !editorRef.current) {
      Alert.alert('Editor Not Open', 'Please open the editor first to send conversation.');
      return;
    }

    try {
      const meaningfulMessages = chatMessages.filter(
        (msg) =>
          msg.text &&
          msg.text.trim().length > 0 &&
          !msg.isDisclaimer &&
          !msg.formSchema &&
          !msg.actionCards &&
          !msg.showOnboardingForm &&
          !msg.showProfileForm &&
          !msg.showBusinessModelSelection &&
          !msg.showModelConfirmation
      );

      if (meaningfulMessages.length === 0) {
        Alert.alert('No Content', 'No conversation content to send.');
        return;
      }

      const conversationHtml = meaningfulMessages
        .map((msg, index) => {
          const role = msg.user._id === globalUser ? 'User' : 'Assistant';
          const timestamp = msg.createdAt ? new Date(msg.createdAt).toLocaleString() : '';
          const text = typeof msg.text === 'string' ? msg.text : JSON.stringify(msg.text);

          return `
            <div style="margin-bottom: 16px; padding: 12px; border-left: 3px solid ${role === 'User' ? '#3b82f6' : '#10b981'}; background-color: ${theme ? '#f8f9fa' : '#1f2937'}; border-radius: 4px;">
              <div style="font-weight: 600; color: ${role === 'User' ? '#3b82f6' : '#10b981'}; margin-bottom: 4px;">
                ${role}
              </div>
              <div style="color: ${theme ? '#333' : '#e0e0e0'}; line-height: 1.6;">
                ${text.replace(/\n/g, '<br>')}
              </div>
              ${timestamp ? `<div style="font-size: 12px; color: ${theme ? '#666' : '#9ca3af'}; margin-top: 4px;">${timestamp}</div>` : ''}
            </div>
          `;
        })
        .join('');

      const fullContent = `<h2>Conversation History</h2>${conversationHtml}`;
      handleSendToEditor(fullContent, 'html');
    } catch (error) {
      console.error('Error sending conversation to editor:', error);
      Alert.alert('Error', 'Failed to send conversation to editor.');
    }
  };
};


